import type { TypedAnalyticsContexts } from '@/analytics/snowplow/analytics-context.types';
import { useAnalyticsTracking } from '@/analytics/snowplow/AnalyticsTrackingContext';
import { useEmitUiEvent } from '@/analytics/snowplow/useEmitUIEvent';
import { useCallback } from 'react';

export const usePrescriptionAssistantTracking = () => {
  const emitEvent = useEmitUiEvent();
  const { uiEvent } = useAnalyticsTracking();
  const contextId = uiEvent?.context_id;

  const trackSelectMedicationReissue = useCallback(
    (prescriptionId: number) => {
      emitEvent({
        component_name: 'ReissueRadioButton',
        event_name: 'select_medication_reissue',
        id: 'select_medication_reissue',
        value: String(prescriptionId),
        context_id: contextId,
      });
    },
    [emitEvent, contextId],
  );

  const trackSelectMedicationCancel = useCallback(
    (prescriptionId: number) => {
      emitEvent({
        component_name: 'CancelRadioButton',
        event_name: 'select_medication_cancel',
        id: 'select_medication_cancel',
        value: String(prescriptionId),
        context_id: contextId,
      });
    },
    [emitEvent, contextId],
  );

  const trackViewPrescriptionHistory = useCallback(
    (prescriptionId: number) => {
      emitEvent({
        component_name: 'PrescriptionHistoryButton',
        event_name: 'view_prescription_history',
        id: 'view_prescription_history',
        value: String(prescriptionId),
        context_id: contextId,
      });
    },
    [emitEvent, contextId],
  );

  const trackAddAdditionalMedication = useCallback(() => {
    emitEvent({
      component_name: 'AddMedicationButton',
      event_name: 'click_add_medication',
      id: 'click_add_medication',
      context_id: contextId,
    });
  }, [emitEvent, contextId]);

  const trackReviewScript = useCallback(
    (consultationId?: number) => {
      emitEvent(
        {
          component_name: 'ReviewScriptButton',
          event_name: 'click_review_script',
          id: 'click_review_script',
          context_id: contextId,
        },
        consultationId
          ? {
              consultation: {
                consultation_id: String(consultationId),
              },
            }
          : undefined,
      );
    },
    [emitEvent, contextId],
  );

  const trackPrescriptionCreated = useCallback(
    ({ prescriptionId, extraContext }: { prescriptionId: number; extraContext?: TypedAnalyticsContexts }) => {
      emitEvent(
        {
          component_name: 'CreatedPrescription',
          event_name: 'created_prescription',
          id: 'created_prescription',
          value: String(prescriptionId),
          context_id: contextId,
        },
        extraContext,
      );
    },
    [emitEvent, contextId],
  );

  return {
    trackSelectMedicationReissue,
    trackSelectMedicationCancel,
    trackViewPrescriptionHistory,
    trackAddAdditionalMedication,
    trackReviewScript,
    trackPrescriptionCreated,
  };
};
